// V1.42 · Bloque de "Listado de valores" para Configuración.
// Sólo lectura para cualquier autenticado; sólo matriz/franquicia guarda.
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, ListChecks } from "lucide-react";

function ValuesBlock({ title, lookupKey, hint }) {
  const [values, setValues] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/settings/lookup/${lookupKey}`);
      setValues(Array.isArray(data?.values) ? data.values : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo cargar");
    }
  }, [lookupKey]);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/settings/lookup/${lookupKey}`, { values: next });
      setValues(data?.values || next);
      toast.success("Listado actualizado");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const add = () => {
    const s = draft.trim();
    if (!s) return;
    if (values.some((v) => v.toLowerCase() === s.toLowerCase())) {
      toast.info("Ya existe ese valor"); return;
    }
    setDraft("");
    save([...values, s]);
  };
  const rename = (i, v) => {
    const next = values.slice(); next[i] = v; setValues(next);
  };
  const remove = (i) => save(values.filter((_, idx) => idx !== i));

  return (
    <div className="card-surface p-4" data-testid={`lookup-${lookupKey}`}>
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-4 w-4 text-orange-600" />
        <div>
          <h3 className="font-semibold text-base">{title}</h3>
          {hint && <p className="text-[11px] text-neutral-500">{hint}</p>}
        </div>
      </div>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={v}
              onChange={(e) => rename(i, e.target.value)}
              onBlur={() => { const next = values.slice(); if (next[i] !== undefined) save(next); }}
              className="flex-1 h-9 rounded-lg border border-neutral-300 px-2 text-sm"
              data-testid={`lookup-${lookupKey}-item-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={saving}
              className="btn-secondary !h-9 !w-9 !p-0 flex items-center justify-center"
              title="Eliminar"
              data-testid={`lookup-${lookupKey}-del-${i}`}
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Nuevo valor…"
            className="flex-1 h-9 rounded-lg border border-neutral-300 px-2 text-sm"
            data-testid={`lookup-${lookupKey}-new`}
          />
          <button
            type="button"
            onClick={add}
            disabled={saving || !draft.trim()}
            className="btn-primary !h-9 !px-3 !text-xs"
            data-testid={`lookup-${lookupKey}-add`}
          >
            <Plus className="h-3.5 w-3.5" /> Añadir
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LookupListsSettings() {
  return (
    <div className="card-surface p-4">
      <div className="mb-3">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-orange-600" /> Listado de valores
        </h3>
        <p className="text-xs text-neutral-500">
          Fuentes centralizadas para desplegables de la aplicación. Añadir, editar y eliminar
          está restringido a Matriz y Franquicia (requiere PIN maestro).
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <ValuesBlock
          title="Profesores · Disposición"
          lookupKey="teacher_unavailability_reasons"
          hint="Motivos disponibles al marcar No disponible al profesor."
        />
        <ValuesBlock
          title="Direcciones · Tipo de vía"
          lookupKey="address_types"
          hint="Valores del desplegable de tipo de vía en direcciones."
        />
        <ValuesBlock
          title="Alumnos · Filtros de listado"
          lookupKey="listado_filters"
          hint="Etiquetas de la hoja imprimible (APTO, ABANDONO, SIN TASAS…). Se asignan en la ficha del alumno."
        />
      </div>
    </div>
  );
}
